import React, { useState, useEffect, useRef } from 'react';
import StepLayout from '../components/StepLayout';
import ApproveOnPhone from '../components/ApproveOnPhone';
import { connectSSE } from '../sse';
import { api } from '../api';

// B-FE-APP-APPROVAL-3 (2026-05-17): same fix as PR #357/#358 — V3 can
// fire needs_app_approval during the session-cleanup phase (LinkedIn
// re-prompts the seller mid-cleanup). Swap to ApproveOnPhone card.
const APPROVAL_OVERLAY_DISABLED =
  process.env.REACT_APP_DISABLE_APP_APPROVAL_OVERLAY === '1';

// B-APPROVAL-RESOLVED (2026-05-17): clear overlay on the new resolved
// events V3 publishes between consecutive challenges. Setting this to
// '1' falls back to overlay-stays-until-terminal behavior.
const RESOLVED_EVENTS_DISABLED =
  process.env.REACT_APP_DISABLE_RESOLVED_EVENTS === '1';

// Step 11 is server-driven. The backend chains run_session_cleanup_task
// from step 10's success block — this screen just listens for the
// cleanup_success SSE event and auto-advances. No POST from the FE.
export default function Step11SessionCleanup({ step, totalSteps, sessionId, setSessionId, sessionData, updateData, onNext, goToStep }) {
  const [statusLines, setStatusLines] = useState(['Terminating other sessions...']);
  const [currentStatus, setCurrentStatus] = useState('Terminating other sessions...');
  const [error, setError] = useState('');
  // B-FE-APP-APPROVAL-3: app-approval overlay state (mid-cleanup case).
  const [showApproveOverlay, setShowApproveOverlay] = useState(false);
  const [approveError, setApproveError] = useState('');
  const sseRef = useRef(null);
  const startedRef = useRef(false);
  const advancedRef = useRef(false);

  // Self-recover: V3 email-change does cleanup inline (Remove old +
  // Make Primary as part of one task). If the session is already past
  // step 11 by the time we mount, skip to Step 12.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const body = await api.getStatus(sessionId);
        if (cancelled || advancedRef.current) return;
        if (body?.currentStep && body.currentStep >= 12) {
          advancedRef.current = true;
          if (goToStep) goToStep(13); else onNext();
        }
      } catch (_) { /* transient */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, goToStep, onNext]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    sseRef.current = connectSSE(sessionId, (event) => {
      // B-FE-APP-APPROVAL-3: swap to ApproveOnPhone when V3 emits
      // needs_app_approval during cleanup. Clear on the existing
      // completion/cleanup events handled below.
      if (event.type === 'needs_app_approval' && !APPROVAL_OVERLAY_DISABLED) {
        // eslint-disable-next-line no-console
        console.log(
          '[B-FE-APPROVAL-3] showing ApproveOnPhone overlay, phase=session_cleanup',
          { sessionId, eventType: event.type },
        );
        setShowApproveOverlay(true);
        setApproveError('');
        return;
      }
      // B-APPROVAL-RESOLVED (2026-05-17): clear overlay between
      // consecutive challenges. The existing email_change_success /
      // cleanup_success branch below stays as the terminal-state
      // safety net; this fires per-challenge instead of per-flow.
      if (
        !RESOLVED_EVENTS_DISABLED
        && (event.type === 'app_approval_resolved'
            || event.type === '2fa_resolved')
      ) {
        // eslint-disable-next-line no-console
        console.log(
          '[B-APPROVAL-RESOLVED] clearing overlay, phase=session_cleanup',
          { sessionId, eventType: event.type },
        );
        setShowApproveOverlay(false);
        return;
      }
      if (event.type === 'progress' || event.type === 'status') {
        const message = event.message || 'Processing...';
        setCurrentStatus(message);
        setStatusLines((prev) => [...prev, message]);
      } else if (
        event.type === 'cleanup_success' ||
        event.type === 'completed' ||
        // V3 mode: email-change task includes cleanup as part of its
        // own flow (Make Primary + Remove). When it finishes, recognize
        // those as completion signals here too.
        event.type === 'email_change_success' ||
        event.type === 'v3_done'
      ) {
        setCurrentStatus('Cleanup complete!');
        setStatusLines((prev) => [...prev, 'Email verified', 'Cleanup complete!']);
        if (sseRef.current) {
          sseRef.current.close();
          sseRef.current = null;
        }
        setTimeout(() => onNext(), 600);
      } else if (event.type === 'error' || event.type === 'cleanup_failed' || event.type === 'v3_blocked') {
        const msg = event.reason || event.message || event.error
          || 'Cleanup failed. Please contact support.';
        setError(msg);
        if (sseRef.current) {
          sseRef.current.close();
          sseRef.current = null;
        }
      }
    });

    // V3 mode: the email-change task fires email_change_success + v3_done
    // BEFORE Step 11 mounts, so the SSE subscription above will never see
    // them. Backstop: after 3s with no terminal event, assume V3 already
    // completed cleanup and advance. Harmless on the legacy path because
    // cleanup_success arrives well within 3s if the legacy task is the
    // one running.
    const backstop = setTimeout(() => {
      if (sseRef.current) {
        setCurrentStatus('Cleanup already complete (V3 path)');
        setStatusLines((prev) => [...prev, 'Cleanup already complete']);
        sseRef.current.close();
        sseRef.current = null;
        onNext();
      }
    }, 3000);

    return () => {
      clearTimeout(backstop);
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, []);

  const spinnerStyle = {
    width: 48,
    height: 48,
    border: '4px solid var(--border)',
    borderTopColor: 'var(--primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  };

  const statusLineStyle = {
    color: 'var(--text2)',
    fontSize: 13,
    padding: '4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const checkStyle = {
    color: 'var(--success, #22c55e)',
    fontSize: 14,
    flexShrink: 0,
  };

  // B-FE-APP-APPROVAL-3: render polished ApproveOnPhone when V3 needs
  // the seller to tap Yes on their phone mid-cleanup.
  if (showApproveOverlay && !error) {
    return (
      <StepLayout title="Approve sign-in on your phone" variant="centered">
        <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 20, textAlign: 'center' }}>
          LinkedIn needs you to confirm this update.
        </p>
        <ApproveOnPhone
          subtitle="We'll continue automatically once LinkedIn confirms your approval."
          error={approveError}
        />
      </StepLayout>
    );
  }

  return (
    <StepLayout title="Session Cleanup">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {!error ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={spinnerStyle} />
          <p style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            {currentStatus}
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 24 }}>
            Please do not close this window.
          </p>

          {statusLines.length > 0 && (
            <div style={{
              background: 'var(--surface)',
              borderRadius: 8,
              padding: 16,
              textAlign: 'left',
            }}>
              {statusLines.map((line, i) => (
                <div key={i} style={statusLineStyle}>
                  <span style={checkStyle}>&#10003;</span>
                  <span>{line}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: 24,
            color: 'var(--danger, #ef4444)',
          }}>
            !
          </div>
          <p style={{ color: 'var(--danger, #ef4444)', fontSize: 15, marginBottom: 8, fontWeight: 600 }}>
            Cleanup Failed
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13 }}>
            {error}
          </p>
        </div>
      )}
    </StepLayout>
  );
}
